import { ArchiveReason, BookingActor, type ArchivedBooking, type Booking, type BookingClient } from '../../../models/Scheduling'
import { formatDateAndTime, formatDateAndTimeRange, formatSentDate, formatSuggestedTime } from '../bookingFormat'
import { clientDisplayName } from '../shared/schedulingDisplay'

export interface HistoryTag {
  label: string
  className: string
}

const RED_TAG = 'zocks-bg-color-danger-background zocks-text-danger-200'
const GRAY_TAG = 'zocks-bg-color-gray-200 zocks-text-gray-900'
const YELLOW_TAG = 'zocks-bg-color-warning zocks-text-gray-900'

// Rows stamped archived carry no trace of what kind of booking was archived (status is already
// cancelled), so the label is derived from the booking's shape: a proposed time means it was a
// booking/reschedule request archived after expiry, otherwise it was an open booking link.
const archivedTag = (booking: ArchivedBooking): HistoryTag => {
  if (booking.originalStart) {
    return { label: 'Expired reschedule request', className: YELLOW_TAG }
  }
  if (booking.start) {
    return { label: 'Expired booking request', className: YELLOW_TAG }
  }
  return { label: 'Archived booking link', className: GRAY_TAG }
}

export const getHistoryTag = (booking: ArchivedBooking): HistoryTag => {
  switch (booking.archiveReason) {
    case ArchiveReason.CANCELLED:
      return { label: 'Canceled meeting', className: RED_TAG }
    case ArchiveReason.DECLINED_BOOKING_REQUEST:
      return { label: 'Declined booking request', className: RED_TAG }
    case ArchiveReason.DECLINED_RESCHEDULE_REQUEST:
      return { label: 'Declined reschedule request', className: RED_TAG }
    case ArchiveReason.DISMISSED:
      return { label: 'Dismissed suggestion', className: GRAY_TAG }
    case ArchiveReason.MEETING_TIME_PASSED:
      return { label: 'Meeting time passed', className: GRAY_TAG }
    case ArchiveReason.ARCHIVED:
      return archivedTag(booking)
    case ArchiveReason.EXPIRED_BOOKING_REQUEST:
      return { label: 'Expired booking request', className: YELLOW_TAG }
    case ArchiveReason.EXPIRED_RESCHEDULE_REQUEST:
      return { label: 'Expired reschedule request', className: YELLOW_TAG }
    case ArchiveReason.EXPIRED_BOOKING_LINK:
      return { label: 'Expired booking link', className: YELLOW_TAG }
  }
}

export type HistoryTimeLine =
  // "was Apr 25, 2026 · 12:30 – 1:30 PM" / "sent Apr 24, 2026 · 10:06 AM" / "proposed Apr 25, 2026 · 12:30 – 1:30 PM"
  | { kind: 'single'; prefix: 'was' | 'sent' | 'proposed' | null; text: string }
  // "was Apr 24 <reschedule icon> proposed Apr 25, 2026 · 12:30 – 1:30 PM"
  | { kind: 'reschedule'; was: string; proposed: string }

const dateAndRange = (booking: ArchivedBooking): string | null => (booking.start ? formatDateAndTimeRange(booking.start, booking.end) : null)

const sentLine = (booking: ArchivedBooking): HistoryTimeLine => ({
  kind: 'single',
  prefix: 'sent',
  text: formatDateAndTime(new Date(booking.createdAt)),
})

const proposedLine = (booking: ArchivedBooking): HistoryTimeLine | null => {
  const proposed = dateAndRange(booking)
  if (!proposed) {
    return null
  }
  if (booking.originalStart) {
    return { kind: 'reschedule', was: formatSentDate(new Date(booking.originalStart)), proposed }
  }
  return { kind: 'single', prefix: 'proposed', text: proposed }
}

export const getHistoryTimeLine = (booking: ArchivedBooking): HistoryTimeLine | null => {
  switch (booking.archiveReason) {
    case ArchiveReason.CANCELLED:
    case ArchiveReason.MEETING_TIME_PASSED: {
      const text = dateAndRange(booking)
      return text ? { kind: 'single', prefix: 'was', text } : null
    }
    case ArchiveReason.DECLINED_BOOKING_REQUEST:
    case ArchiveReason.DECLINED_RESCHEDULE_REQUEST:
    case ArchiveReason.EXPIRED_BOOKING_REQUEST:
    case ArchiveReason.EXPIRED_RESCHEDULE_REQUEST:
      return proposedLine(booking)
    case ArchiveReason.ARCHIVED:
      return booking.start ? proposedLine(booking) : sentLine(booking)
    case ArchiveReason.EXPIRED_BOOKING_LINK:
      return sentLine(booking)
    case ArchiveReason.DISMISSED:
      return { kind: 'single', prefix: null, text: formatSuggestedTime({ start: booking.start, end: booking.end, suggestedDate: booking.suggestedDate }) }
  }
}

export type HistoryActor = { kind: 'user'; userId: string } | { kind: 'client'; contactId: string } | { kind: 'allClients' } | { kind: 'unattributed' }

export interface HistoryAttribution {
  verb: 'Canceled' | 'Declined' | 'Dismissed' | 'Archived' | null
  actor: HistoryActor
  closedAt: string
  requestedOn: string | null
  noResponse: boolean
}

const VERBS: Record<ArchiveReason, HistoryAttribution['verb']> = {
  [ArchiveReason.CANCELLED]: 'Canceled',
  [ArchiveReason.DECLINED_BOOKING_REQUEST]: 'Declined',
  [ArchiveReason.DECLINED_RESCHEDULE_REQUEST]: 'Declined',
  [ArchiveReason.DISMISSED]: 'Dismissed',
  [ArchiveReason.ARCHIVED]: 'Archived',
  [ArchiveReason.MEETING_TIME_PASSED]: null,
  [ArchiveReason.EXPIRED_BOOKING_REQUEST]: null,
  [ArchiveReason.EXPIRED_RESCHEDULE_REQUEST]: null,
  [ArchiveReason.EXPIRED_BOOKING_LINK]: null,
}

const REQUEST_REASONS = new Set([
  ArchiveReason.DECLINED_BOOKING_REQUEST,
  ArchiveReason.DECLINED_RESCHEDULE_REQUEST,
  ArchiveReason.EXPIRED_BOOKING_REQUEST,
  ArchiveReason.EXPIRED_RESCHEDULE_REQUEST,
])

const getActor = (booking: ArchivedBooking): HistoryActor => {
  const closedBy = booking.closedBy
  if (!closedBy) {
    // Legacy rows closed before attribution shipped.
    return { kind: 'unattributed' }
  }
  if (closedBy.actor === BookingActor.USER) {
    // A user actor without an id means the organizer deleted the calendar event (calendar-sync
    // cancel) — the only user action without a caller id; never render it as "(me)".
    return closedBy.id ? { kind: 'user', userId: closedBy.id } : { kind: 'unattributed' }
  }
  if (closedBy.id) {
    return { kind: 'client', contactId: closedBy.id }
  }
  // The public manage token is booking-level, so a multi-client cancel (and the calendar
  // all-declined path) is unattributable to a single contact.
  return booking.archiveReason === ArchiveReason.CANCELLED ? { kind: 'allClients' } : { kind: 'unattributed' }
}

export interface ActorNameContext {
  clients: BookingClient[]
  currentUserId: string
  resolveUserName: (userId: string) => string | undefined
}

// A client actor is always one of the booking's own clients — no extra lookup needed.
const resolveClientName = (clients: BookingClient[], contactId: string): string | undefined => {
  const client = clients.find((bookingClient) => bookingClient.contactId === contactId)
  return client ? clientDisplayName(client) : undefined
}

export const resolveActorName = (actor: HistoryActor, { clients, currentUserId, resolveUserName }: ActorNameContext): string | undefined => {
  switch (actor.kind) {
    case 'user': {
      const name = resolveUserName(actor.userId)
      if (!name) {
        return undefined
      }
      return actor.userId === currentUserId ? `${name} (me)` : name
    }
    case 'client':
      return resolveClientName(clients, actor.contactId)
    case 'allClients':
      return 'all clients'
    case 'unattributed':
      return undefined
  }
}

// The cancellation note is authored by the closing person; system closures (all clients,
// calendar sync, legacy rows) carry a system sentence instead and render without an author.
export const getCancellationAuthor = (attribution: HistoryAttribution, context: ActorNameContext): string | undefined =>
  attribution.actor.kind === 'user' || attribution.actor.kind === 'client' ? resolveActorName(attribution.actor, context) : undefined

export const getClientCancellationAuthor = (booking: Pick<Booking, 'closedBy' | 'clients'>): string | undefined => {
  const closedBy = booking.closedBy
  if (closedBy?.actor !== BookingActor.CLIENT || !closedBy.id) {
    return undefined
  }
  return resolveClientName(booking.clients, closedBy.id)
}

export const getHistoryAttribution = (booking: ArchivedBooking): HistoryAttribution => {
  const verb = VERBS[booking.archiveReason]
  // Archiving an already-expired request stamps closedReason=archived, so expired rows with an
  // attributed closer surface it ("Expired booking request — Archived by …" in the design).
  const isStampedArchive = booking.archiveReason === ArchiveReason.ARCHIVED
  return {
    verb,
    actor: verb ? getActor(booking) : { kind: 'unattributed' },
    // closedAt is null on legacy rows (fall back to updatedAt); derived expired/time-passed rows
    // have no closedAt at all — archivedAt carries the expiry/meeting-end time.
    closedAt: booking.closedAt ?? (verb ? booking.updatedAt : booking.archivedAt),
    requestedOn: REQUEST_REASONS.has(booking.archiveReason) || (isStampedArchive && booking.start) ? booking.createdAt : null,
    noResponse: booking.archiveReason === ArchiveReason.EXPIRED_BOOKING_LINK,
  }
}
